<script lang="ts">
	import Arrow from "@components/common/Arrow.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { portal } from "svelte-portal";
	import { on } from "svelte/events";
	import closeIcon from "@core/assets/icons/close.svg?raw";
	import dropdownIcon from "@core/assets/icons/dropdown-arrrow.svg?raw";
	import menuIcon from "@core/assets/icons/menu.svg?raw";
	import type { SubmenuItem } from "@core/schema/MenuItem.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import AlgoliaSearchbar from "./AlgoliaSearchbar.svelte";
	import LanguageSwitcher from "./LanguageSwitcher.svelte";

	let {
		mobileOpen = $bindable(false),
		searchOpen = $bindable(false),
		searchText = $bindable(""),
	}: { mobileOpen?: boolean; searchOpen: boolean; searchText: string } = $props();

	const { pagePath, tenant, menu } = getGlobalTemplateProps();

	$effect(() => {
		if (!mobileOpen) {
			return;
		}
		return on(document.body, "mousedown", (): void => {
			mobileOpen = false;
		});
	});
</script>

{#snippet submenuItemInner({ name, items }: SubmenuItem)}
	<span class="text-cmcnavy-80 text-xs/4 uppercase font-medium">{name}</span>
	<div class="flex flex-col">
		{#each items as item (item)}
			<a
				class="font-semibold cursor-pointer text-base/9 group hover:pl-9 transition-all line-clamp-1 relative"
				href={item.href}
				target={item.target}
			>
				{item.name}
				<Arrow
					class="size-8 text-cmcprimary absolute left-0 top-1/2 -translate-y-1/2 [&:not(:is(a:hover>*))]:invisible [&:not(:is(a:hover>*))]:opacity-0 transition-[visibility,opacity]"
				/>
			</a>
		{/each}
	</div>
{/snippet}

<nav class={["text-cmctext hidden 2xl:gap-4 items-center", !searchOpen && "xl:flex"]}>
	{#each menu as item (item)}
		{@const isActive = item.href === pagePath[tenant.locale]}
		<div class="relative inline-flex gap-2 px-2 cursor-pointer group">
			<a
				class="font-semibold tracking-widest text-xs/[5rem] uppercase group-hover:text-cmcprimary transition-colors relative"
				href={item.href}
				target={item.target}
			>
				<span>{item.name}</span>
				<div
					class={[
						"absolute bottom-6 bg-cmcprimary h-[1.5px]",
						isActive ? "w-full" : "group-hover:w-full w-0 transition-all",
					]}
				></div>
			</a>
			{#if item.submenuItems && item.submenuItems.length > 0}
				<button type="button">
					<Icon icon={dropdownIcon} class="h-2 group-hover:rotate-180 transition-transform" />
				</button>
				<div class="submenu">
					<div class="rounded-b-3xl bg-white backdrop-blur-xl grid grid-cols-4 gap-4 p-4">
						<div class="p-6 text-cmcsecondary font-druk-cond uppercase text-5xl">
							{item.name}
						</div>
						{#each item.submenuItems as submenuItem (submenuItem)}
							<div class="p-4 flex-col gap-4 flex">
								{void (/* eslint-disable-next-line @typescript-eslint/no-confusing-void-expression */ "Snippets :/")}
								{@render submenuItemInner(submenuItem)}
							</div>
						{/each}
					</div>
				</div>
			{/if}
		</div>
	{/each}
</nav>

<button
	class={["flex gap-2 xl:hidden", mobileOpen ? "text-cmcprimary" : "text-cmctext"]}
	onclick={(): void => {
		mobileOpen = !mobileOpen;
	}}
>
	<span class="font-semibold text-xs/8 uppercase tracking-widest">Menu</span>
	<div class="size-8 items-center justify-center flex" aria-hidden="true">
		<Icon icon={mobileOpen ? closeIcon : menuIcon} />
	</div>
</button>

<dialog
	open={mobileOpen}
	use:portal={document.body}
	class={[
		"open:flex xl:!hidden [:has(&:open)]:!overflow-hidden overflow-y-auto max-h-svh overflow-x-hidden max-w-full w-full",
		"bg-white/75 backdrop-blur-lg z-30 p-3 flex-col transition-transform fixed inset-x-0 top-12 pt-15 shadow-xl gap-4 mb-auto",
	]}
	onmousedown={(e: MouseEvent): void => {
		e.stopPropagation();
	}}
>
	<div class="flex items-center gap-4">
		<AlgoliaSearchbar bind:open={searchOpen} bind:searchText />
		<LanguageSwitcher class="w-fit shrink-0" />
	</div>
	{#each menu as item (item)}
		{@const isActive = item.href === pagePath[tenant.locale]}
		{#if item.submenuItems && item.submenuItems.length > 0}
			<details class="group cursor-pointer">
				<summary class="list-none marker:hidden relative h-8 [&::-webkit-details-marker]:hidden">
					<div class="flex justify-between">
						<a href={item.href} class="uppercase font-semibold text-xs/8">{item.name}</a>
						<Icon icon={dropdownIcon} class="h-2 group-open:rotate-180 transition-transform" />
					</div>
					<div class="absolute bottom-0 bg-cmcprimary h-[1.5px] group-open:w-full w-0 transition-all"></div>
				</summary>
				<div class="flex flex-col gap-2 my-4">
					{#each item.submenuItems as submenuItem (submenuItem)}
						{#if submenuItem.items.length > 0}
							<div class="py-4 px-2 flex-col gap-4 flex rounded-lg bg-white">
								{void (/* eslint-disable-next-line @typescript-eslint/no-confusing-void-expression */ "Snippets :/")}
								{@render submenuItemInner(submenuItem)}
							</div>
						{/if}
					{/each}
				</div>
			</details>
		{:else}
			<a
				class="font-semibold cursor-pointer tracking-widest text-xs/8 uppercase group-active:text-cmcprimary transition-colors relative w-fit"
				href={item.href}
				target={item.target}
			>
				<span>{item.name}</span>
				{#if isActive}
					<div class="absolute bottom-0 bg-cmcprimary h-[1.5px] w-full"></div>
				{/if}
			</a>
		{/if}
	{/each}
</dialog>

<style lang="postcss">
	.submenu {
		@apply pointer-events-none fixed left-0 top-full z-50 w-full min-w-full -translate-y-2 cursor-default opacity-0 transition-all group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100;
	}
</style>
